import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  MenuItem,
  UserRole,
  ProjectConfig,
  DepartmentKey,
  DepartmentConfig,
} from '@/lib/types/authorization';

/**
 * Organization information from FHIR Organization resource
 */
export interface Organization {
  id: string;
  name: string;
  active?: boolean;
  type?: string;
  telecom?: Array<{
    system?: string;
    value?: string;
  }>;
  address?: Array<{
    line?: string[];
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  }>;
}

/**
 * User information returned from the backend API
 */
export interface User {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** User role code from the backend (e.g. 'practitioner', 'frontdesk', 'admin') */
  role?: UserRole | string;
  /** Display label for the user's role */
  roleDisplay?: string;
  /**
   * What the role may do (see CAPABILITIES), from its AccessPolicy. Undefined for
   * a session saved before this existed, until it is refreshed from the backend.
   */
  capabilities?: string[];
  specialization?: string;
  practitionerRole?: {
    specialty?: string;
    department?: string;
    practitionerRoleId?: string;
  } | null;
  projectName?: string;
  /**
   * Stable Medplum Project ID (UUID) the user is scoped to, extracted from
   * `data.project.reference` ("Project/<id>") at login. Unlike `projectName`
   * (a mutable display string), this ID is stable across project renames and
   * is the correct value to gate hospital-specific UI on.
   */
  projectId?: string;
  /** Menu items from UserConfiguration */
  menu?: MenuItem[];
}

/**
 * Authentication state interface
 */
interface AuthState {
  /**
   * Currently authenticated user, or null if not logged in
   */
  user: User | null;

  /**
   * JWT access token
   */
  token: string | null;

  /**
   * Per-project configuration (features, labels, Medplum URL).
   * Set after login, null when logged out.
   */
  projectConfig: ProjectConfig | null;

  /**   * List of organizations available to the user
   */
  organizations: Organization[];

  /**
   * Currently selected organization
   */
  selectedOrganization: Organization | null;

  /**   * Whether the auth state has been initialized from localStorage
   * Department key identifying the doctor's department (e.g., "cardiology", "pediatrics").
   * Set after login, null when logged out or department not recognized.
   */
  departmentKey: DepartmentKey;

  /**
   * Department-specific case-sheet UI configuration.
   * Controls field order and visibility rules per department.
   * Set after login, null when logged out or no config available.
   */
  departmentConfig: DepartmentConfig | null;

  /**
   * Whether the auth state has been initialized from localStorage
   */
  isInitialized: boolean;

  /**
   * Whether a login/logout operation is in progress
   */
  isLoading: boolean;

  /**
   * Error message from last auth operation
   */
  error: string | null;

  /**
   * Timestamp when the user logged in (for session countdown timer)
   */
  loginTimestamp: number | null;

  /**
   * Whether the session timer is paused
   */
  isTimerPaused: boolean;

  /**
   * Timestamp when the timer was paused (to calculate elapsed pause duration)
   */
  pausedAt: number | null;
}

/**
 * Authentication actions interface
 */
interface AuthActions {
  /**
   * Set the authenticated user and token
   * Note: Refresh token is handled via HTTP-Only cookie, not stored in frontend
   * @param user - User object
   * @param token - JWT access token
   * @param projectConfig - Optional per-project configuration
   * @param departmentKey - Optional department key
   * @param departmentConfig - Optional department-specific case-sheet configuration
   */
  setAuth: (
    user: User,
    token: string,
    projectConfig?: ProjectConfig,
    departmentKey?: DepartmentKey,
    departmentConfig?: DepartmentConfig | null
  ) => void;

  /**
   * Update the access token (for token refresh)
   * Note: Refresh token is handled via HTTP-Only cookie
   * @param token - New JWT access token
   */
  updateToken: (token: string) => void;

  /**
   * Update projectConfig independently (e.g., after session restore)
   */
  setProjectConfig: (config: ProjectConfig) => void;

  /**
   * Set the list of organizations
   * @param organizations - Array of Organization objects
   */
  setOrganizations: (organizations: Organization[]) => void;

  /**
   * Set the selected organization
   * @param organization - Organization to select, or null to clear
   */
  setSelectedOrganization: (organization: Organization | null) => void;

  /**
   * Sync user configuration from backend after page refresh.
   * Fetches the latest UserConfiguration, menu, and role from backend.
   * Updates the user object in the store.
   * If the token is expired or invalid, clears auth and returns false.
   * @returns Promise<boolean> - true if sync succeeded, false if auth was cleared
   */
  syncUserConfiguration: () => Promise<boolean>;

  /**
   * Validate the current token and clear auth if expired or missing.
   * Called on app initialization to ensure the session is still valid.
   * @returns boolean - true if token is valid, false if auth was cleared
   */
  validateSession: () => boolean;

  /**
   * Update department configuration independently (e.g., after session restore or admin preview)
   */

  setDepartmentConfig: (
    departmentKey: DepartmentKey,
    departmentConfig: DepartmentConfig | null
  ) => void;

  /**
   * Clear authentication state (logout)
   */
  clearAuth: () => void;

  /**
   * Set loading state
   * @param isLoading - Loading state
   */
  setLoading: (isLoading: boolean) => void;

  /**
   * Set error message
   * @param error - Error message or null to clear
   */
  setError: (error: string | null) => void;

  /**
   * Update user information
   * @param user - Partial user object to merge
   */
  updateUser: (user: Partial<User>) => void;

  /**
   * Check if user is authenticated
   * @returns true if user is logged in
   */
  isAuthenticated: () => boolean;

  /**
   * Check if user has a specific role
   * @param role - Role to check
   * @returns true if user has the role
   */
  hasRole: (role: string) => boolean;

  /**
   * Get menu items for current user
   * @returns MenuItem array from UserConfiguration or default for role
   */
  getMenu: () => MenuItem[];

  /**
   * Check if user can access a specific route
   * @param route - Route path to check
   * @returns true if user can access the route
   */
  canAccessRoute: (route: string) => boolean;

  /**
   * Toggle the session timer pause state
   */
  toggleTimerPause: () => void;
}

/**
 * Combined auth store type
 */
type AuthStore = AuthState & AuthActions;

/**
 * Initial state
 */
const initialState: AuthState = {
  user: null,
  loginTimestamp: null,
  isTimerPaused: false,
  pausedAt: null,
  token: null,
  projectConfig: null,
  organizations: [],
  selectedOrganization: null,
  departmentKey: null,
  departmentConfig: null,
  isInitialized: false,
  isLoading: false,
  error: null,
};

/**
 * Authentication Store
 *
 * Manages user authentication state including:
 * - User information
 * - JWT token
 * - Loading and error states
 * - Permission and role checking
 *
 * State is persisted to localStorage automatically.
 *
 * @example
 * ```tsx
 * // Login
 * const { setAuth, setLoading, setError } = useAuthStore();
 * setLoading(true);
 * try {
 *   const response = await loginApi(credentials);
 *   setAuth(response.user, response.token);
 * } catch (error) {
 *   setError(error.message);
 * } finally {
 *   setLoading(false);
 * }
 *
 * // Check authentication
 * const { isAuthenticated, user } = useAuthStore();
 * if (isAuthenticated()) {
 *   logger.log(`Logged in as ${user.username}`);
 * }
 *
 * // Check role\n * const { hasRole } = useAuthStore();\n * if (hasRole('admin')) {
 *   // Show edit button
 * }
 *
 * // Logout
 * const { clearAuth } = useAuthStore();
 * clearAuth();
 * ```
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Actions
      setAuth: (user, token, projectConfig, departmentKey, departmentConfig) => {
        set({
          user,
          token,
          projectConfig: projectConfig ?? null,
          departmentKey: departmentKey ?? null,
          departmentConfig: departmentConfig ?? null,
          isInitialized: true,
          loginTimestamp: Date.now(),
          isLoading: false,
          error: null,
        });
        // Also store token separately for easy access by fetch calls
        if (token) {
          localStorage.setItem('auth-token', token);
        }
        // Note: Refresh token is handled via HTTP-Only cookie by the browser
      },

      updateToken: (token) => {
        set({ token });
        // Update the separate token storage
        if (token) {
          localStorage.setItem('auth-token', token);
        }
        // Note: Refresh token is handled via HTTP-Only cookie by the browser
      },

      setProjectConfig: (config) => {
        set({ projectConfig: config });
      },

      setOrganizations: (organizations) => {
        set({ organizations });
      },

      setSelectedOrganization: (organization) => {
        set({ selectedOrganization: organization });
        // Persist selected organization id for session continuity
        if (organization) {
          localStorage.setItem('selected-organization-id', organization.id);
        } else {
          localStorage.removeItem('selected-organization-id');
        }
      },

      validateSession: () => {
        const { token, user } = get();

        // No token or user - session is invalid
        if (!token || !user) {
          return false;
        }

        // Check if token is expired by decoding JWT
        try {
          const parts = token.split('.');
          if (parts.length !== 3) {
            return false;
          }

          const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
          const payload = JSON.parse(atob(base64));
          const exp = payload.exp;

          if (exp) {
            const now = Math.floor(Date.now() / 1000);
            if (exp <= now) {
              // Token is expired
              return false;
            }
          }

          // Token is valid
          return true;
        } catch (error) {
          // Failed to decode token - treat as invalid
          console.error('Failed to validate token:', error);
          return false;
        }
      },

      syncUserConfiguration: async () => {
        const { user, validateSession, clearAuth } = get();

        // First validate the session
        if (!validateSession()) {
          clearAuth();
          return false;
        }

        // After validation, user should exist, but TypeScript doesn't know that
        if (!user) {
          return false;
        }

        try {
          // Import dynamically to avoid circular dependency
          const { fetchUserConfiguration } = await import('@/lib/api/auth');
          const { toast } = await import('@/lib/utils/toast');
          const result = await fetchUserConfiguration();

          // Handle 401 unauthorized - show toast and clear auth
          if (result.status === 'unauthorized') {
            toast.error('Session expired. Please login again.');
            clearAuth();
            return false;
          }

          // Handle other errors - don't clear auth, just return false
          if (result.status === 'error') {
            return false;
          }

          const config = result.data;

          // Update user with fresh configuration from backend
          // The /v2/auth/me endpoint returns the complete user profile
          // Merge into the user as it is NOW, not the copy captured before the
          // await: login() may have added practitionerRole in the meantime, and
          // merging into the stale copy would wipe it (and the Ayurveda UI with it).
          const currentUser = get().user ?? user;
          const updatedUser: User = {
            ...currentUser,
            id: config.id ?? currentUser.id,
            username: config.username ?? currentUser.username,
            email: config.email ?? currentUser.email,
            role: config.role ?? currentUser.role,
            roleDisplay: config.roleDisplay ?? currentUser.roleDisplay,
            capabilities: config.capabilities ?? currentUser.capabilities,
            menu: config.menu ?? currentUser.menu,
            projectName: config.project?.display ?? currentUser.projectName,
          };

          set({ user: updatedUser });
          return true;
        } catch (error) {
          // Silently fail - don't disrupt the user experience if sync fails
          console.error('Failed to sync user configuration:', error);
          return false;
        }
      },

      setDepartmentConfig: (departmentKey, departmentConfig) => {
        set({ departmentKey, departmentConfig });
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          projectConfig: null,
          departmentKey: null,
          departmentConfig: null,
          isLoading: false,
          loginTimestamp: null,
          isTimerPaused: false,
          pausedAt: null,
          error: null,
        });
        // Clear the separate token storage
        localStorage.removeItem('auth-token');
        localStorage.removeItem('project-reference');
        // Note: HTTP-Only cookie for refresh token is cleared by browser
        // when backend sends Set-Cookie with Max-Age=0
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error, isLoading: false });
      },

      updateUser: (userUpdate) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              ...userUpdate,
            },
          });
        }
      },

      isAuthenticated: () => {
        const { user, token } = get();
        return !!user && !!token;
      },

      hasRole: (role) => {
        const { user } = get();
        if (!user || !user.role) return false;
        return user.role === role;
      },

      /**
       * Get menu items for current user
       * Returns user's configured menu from UserConfiguration
       * NO FALLBACK - menu must be created during onboarding
       */
      getMenu: () => {
        const { user } = get();
        if (!user) return [];

        // ONLY return menu from UserConfiguration
        if (user.menu && user.menu.length > 0) {
          return user.menu;
        }

        // NO FALLBACK - menu must be created during onboarding
        // No menu found - onboarding may be incomplete
        return [];
      },

      /**
       * Check if user can access a specific route
       * Routes are accessible if:
       * 1. They appear directly in the user's menu
       * 2. They are child routes under a parent menu item
       */
      canAccessRoute: (route: string) => {
        const { user } = get();
        if (!user) return false;

        const menu = get().getMenu();
        const menuLinks = menu.map((item) => item.link);

        // Direct match or nested route under a menu link
        const hasMenuAccess = menuLinks.some(
          (link) => route === link || route.startsWith(link + '/')
        );
        if (hasMenuAccess) return true;

        // Child routes: only require a parent menu item in the user's menu
        const CHILD_ROUTES: Array<{ path: string; parentMenu: string }> = [
          { path: '/patient-record', parentMenu: '/records' },
          { path: '/patient-form', parentMenu: '/records' },
          { path: '/consultation', parentMenu: '/appointments' },
          { path: '/case-sheet', parentMenu: '/appointments' },
          { path: '/onboarding', parentMenu: '/practitioners' },
          { path: '/admin-dashboard', parentMenu: '/dashboard' },
        ];

        return CHILD_ROUTES.some((child) => {
          const routeMatches = route === child.path || route.startsWith(child.path + '/');
          if (!routeMatches) return false;
          return menuLinks.includes(child.parentMenu);
        });
      },

      toggleTimerPause: () => {
        const state = get();
        if (state.isTimerPaused) {
          // Resuming: shift loginTimestamp forward by the pause duration
          const pauseDuration = state.pausedAt ? Date.now() - state.pausedAt : 0;
          set({
            isTimerPaused: false,
            pausedAt: null,
            loginTimestamp: state.loginTimestamp
              ? state.loginTimestamp + pauseDuration
              : state.loginTimestamp,
          });
        } else {
          // Pausing: record when we paused
          set({ isTimerPaused: true, pausedAt: Date.now() });
        }
      },
    }),

    {
      name: 'cdss-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        projectConfig: state.projectConfig,
        organizations: state.organizations,
        selectedOrganization: state.selectedOrganization,
        departmentKey: state.departmentKey,
        departmentConfig: state.departmentConfig,
        loginTimestamp: state.loginTimestamp,
        isTimerPaused: state.isTimerPaused,
        pausedAt: state.pausedAt,
      }),
      onRehydrateStorage: () => (state) => {
        // Mark as initialized after rehydration (whether there's data or not)
        if (state) {
          state.isInitialized = true;
        }
      },
    }
  )
);

/**
 * Selectors for common auth state queries
 */
export const authSelectors = {
  /**
   * Get current user
   */
  user: (state: AuthStore) => state.user,

  /**
   * Get auth token
   */
  token: (state: AuthStore) => state.token,

  /**
   * Get loading state
   */
  isLoading: (state: AuthStore) => state.isLoading,

  /**
   * Get error message
   */
  error: (state: AuthStore) => state.error,

  /**
   * Get authentication status
   */
  isAuthenticated: (state: AuthStore) => state.isAuthenticated(),
};
